import SecureAuth from "../utils/SecureAuth";

const AUTH_KEY = "bestcall_auth_token";
const EXPIRATION_KEY = "bestcall_auth_expiration";
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Authentication service for managing login state with localStorage
 */
class AuthService {
  /**
   * Login with password and store authentication data
   * @param {string} password - The password to validate
   * @returns {boolean} - True if login successful, false otherwise
   */
  static login(password) {
    try {
      // Use secure authentication utility
      const isValid = SecureAuth.login(password);

      if (isValid) {
        const now = new Date().getTime();
        const expirationTime = now + SESSION_DURATION;

        // Store auth token and expiration
        localStorage.setItem(AUTH_KEY, "authenticated");
        localStorage.setItem(EXPIRATION_KEY, expirationTime.toString());

        return true;
      }

      return false;
    } catch (error) {
      throw error; // Re-throw to handle rate limiting messages
    }
  }

  /**
   * Check if user is currently authenticated and session is valid
   * @returns {boolean} - True if authenticated and session valid, false otherwise
   */
  static isAuthenticated() {
    try {
      const authToken = localStorage.getItem(AUTH_KEY);
      const expiration = localStorage.getItem(EXPIRATION_KEY);

      if (!authToken || !expiration) {
        return false;
      }

      const now = new Date().getTime();
      const expirationTime = parseInt(expiration, 10);

      // Check if session has expired
      if (now > expirationTime) {
        this.logout(); // Clean up expired session
        return false;
      }

      return authToken === "authenticated";
    } catch (error) {
      return false;
    }
  }

  /**
   * Extend the current session by 30 minutes
   * Only works if user is currently authenticated
   */
  static extendSession() {
    if (this.isAuthenticated()) {
      const now = new Date().getTime();
      const newExpirationTime = now + SESSION_DURATION;
      localStorage.setItem(EXPIRATION_KEY, newExpirationTime.toString());
    }
  }

  /**
   * Logout and clear authentication data
   */
  static logout() {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(EXPIRATION_KEY);
  }

  /**
   * Get remaining session time in minutes
   * @returns {number} - Minutes remaining, 0 if not authenticated
   */
  static getSessionTimeRemaining() {
    if (!this.isAuthenticated()) {
      return 0;
    }

    try {
      const expiration = localStorage.getItem(EXPIRATION_KEY);
      if (!expiration) return 0;

      const now = new Date().getTime();
      const expirationTime = parseInt(expiration, 10);
      const remainingTime = Math.max(0, expirationTime - now);

      return Math.floor(remainingTime / (60 * 1000)); // Convert to minutes
    } catch (error) {
      return 0;
    }
  }

  /**
   * Set up automatic session extension on user activity
   * This will extend the session when user interacts with the page
   */
  static setupAutoSessionExtension() {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];
    let lastActivity = Date.now();

    const handleActivity = () => {
      const now = Date.now();
      // Only extend session if it's been more than 5 minutes since last extension
      if (now - lastActivity > 5 * 60 * 1000) {
        this.extendSession();
        lastActivity = now;
      }
    };

    // Add event listeners for user activity
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Set up periodic session check (every minute)
    setInterval(() => {
      if (!this.isAuthenticated()) {
        // Session expired, handle silently
      }
    }, 60 * 1000);
  }

  /**
   * Get session info for debugging
   * @returns {object} - Session information
   */
  static getSessionInfo() {
    return {
      isAuthenticated: this.isAuthenticated(),
      timeRemaining: this.getSessionTimeRemaining(),
      expirationTime: localStorage.getItem(EXPIRATION_KEY),
    };
  }
}

export default AuthService;
